import { asPlaybookId } from "@repo/app-types/entities/playbook";
import {
  asTeammateId,
  isTeammateATeamMember,
} from "@repo/app-types/entities/teammate";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageHeaderFilterBarSlot } from "@repo/ui/Navigation/pageHeaderFilterBarSlot";
import type { FC } from "react";
import { Outlet, useParams } from "react-router";
import { useBasicTeammateQuery } from "../api/entities/teammate.js";
import { TabbedPageLayout } from "../components/layout/pageLayout.js";
import { RouteTabs } from "../components/layout/routeTabs.js";
import { selectTeammate } from "../features/teammate/teammateSlice.js";
import { renderTeammateName } from "../features/teammatePerformance/teammateHelpers.js";
import { useAppSelector } from "../hooks/hook.js";

export const TrainingTeammatePage: FC = () => {
  const { playbookId: playbookIdParam, teammateId: teammateIdParam } =
    useParams();
  if (!teammateIdParam) {
    throw new Error("Missing param teammateId.");
  }
  if (!playbookIdParam) {
    throw new Error("Missing param playbookId.");
  }

  const teammateId = asTeammateId(teammateIdParam);
  const playbookId = asPlaybookId(playbookIdParam);

  const { data: teammate } = useBasicTeammateQuery(teammateId);

  const currentTeammate = useAppSelector(selectTeammate);
  const isCurrentTeammateAMemberOnly =
    currentTeammate && isTeammateATeamMember(currentTeammate);

  const header = (
    <PageHeader
      title={teammate ? renderTeammateName(teammate) : null}
      isLoading={!teammate}
      tabs={
        <RouteTabs
          ariaLabel="Teammate sections"
          tabs={[
            { label: "Overview", to: "." },
            { label: "Customers", to: "customers" },
            {
              label: "Calls",
              to: "customer-interactions",
              matchDescendants: true,
            },
            {
              label: "Achievements",
              to: "achievements",
              matchDescendants: true,
            },
            ...(isCurrentTeammateAMemberOnly
              ? [
                  {
                    label: "Notifications",
                    to: "automation-results",
                    matchDescendants: true,
                  },
                ]
              : []),
          ]}
        />
      }
      filterBar={<PageHeaderFilterBarSlot />}
    />
  );

  return (
    <TabbedPageLayout header={header}>
      <Outlet />
    </TabbedPageLayout>
  );
};
